import { forwardRef, type ReactNode } from 'react';
import { Pressable, StyleSheet, View, type ViewProps } from 'react-native';
import { colors } from '../../theme/colors';
import { tokens } from '../../theme/tokens';
import { Text } from './Text';

type BadgeVariant = 'filled' | 'outline' | 'subtle';
type BadgeColor = 'cyan' | 'green' | 'red' | 'amber' | 'purple' | 'blue' | 'neutral';
type BadgeSize = 'sm' | 'md';

interface BadgeProps extends ViewProps {
  variant?: BadgeVariant;
  color?: BadgeColor;
  icon?: ReactNode;
  onRemove?: () => void;
  removeAccessibilityLabel?: string;
  removeTestID?: string;
  children: ReactNode;
  size?: BadgeSize;
}

interface ColorValues {
  main: string;
  bg: string;
  text: string;
}

const COLOR_MAP: Record<BadgeColor, ColorValues> = {
  cyan: {
    main: colors.primary[500],
    bg: colors.primary.muted,
    text: colors.primary[500],
  },
  green: {
    main: colors.status.success.main,
    bg: colors.status.success.bg,
    text: colors.status.success.light,
  },
  red: {
    main: colors.status.error.main,
    bg: colors.status.error.bg,
    text: colors.status.error.light,
  },
  amber: {
    main: colors.status.warning.main,
    bg: colors.status.warning.bg,
    text: colors.status.warning.light,
  },
  purple: {
    main: colors.accent.purple[500],
    bg: colors.accent.purple.bg,
    text: colors.accent.purple[300],
  },
  blue: {
    main: colors.accent.blue[500],
    bg: colors.status.info.bg,
    text: colors.accent.blue[300],
  },
  neutral: {
    main: colors.neutral[500],
    bg: colors.interactive.hoverSubtle,
    text: colors.neutral[300],
  },
};

const getVariantStyles = (
  variant: BadgeVariant,
  colorValues: ColorValues,
): {
  backgroundColor: string;
  borderWidth: number;
  borderColor: string;
  textColor: string;
} => {
  switch (variant) {
    case 'filled':
      return {
        backgroundColor: colorValues.main,
        borderWidth: 0,
        borderColor: 'transparent',
        textColor: colors.background.primary,
      };
    case 'outline':
      return {
        backgroundColor: 'transparent',
        borderWidth: 1,
        borderColor: colorValues.main,
        textColor: colorValues.text,
      };
    case 'subtle':
      return {
        backgroundColor: colorValues.bg,
        borderWidth: 0,
        borderColor: 'transparent',
        textColor: colorValues.text,
      };
  }
};

export const Badge = forwardRef<View, BadgeProps>(
  (
    {
      variant = 'subtle',
      color = 'cyan',
      icon,
      onRemove,
      removeAccessibilityLabel,
      removeTestID,
      children,
      size = 'md',
      style,
      ...rest
    },
    ref,
  ) => {
    const colorValues = COLOR_MAP[color];
    const variantStyles = getVariantStyles(variant, colorValues);
    const isSmall = size === 'sm';

    return (
      <View
        ref={ref}
        style={[
          styles.container,
          isSmall ? styles.sizeSm : styles.sizeMd,
          {
            backgroundColor: variantStyles.backgroundColor,
            borderWidth: variantStyles.borderWidth,
            borderColor: variantStyles.borderColor,
          },
          style,
        ]}
        accessibilityRole="text"
        {...rest}
      >
        {icon && (
          <View style={[styles.iconWrapper, isSmall ? styles.iconSm : styles.iconMd]}>{icon}</View>
        )}
        {typeof children === 'string' ? (
          <Text
            style={[
              styles.text,
              {
                fontSize: isSmall ? tokens.fontSize.xs : tokens.fontSize.sm,
                color: variantStyles.textColor,
              },
            ]}
          >
            {children}
          </Text>
        ) : (
          children
        )}
        {onRemove && (
          <Pressable
            onPress={onRemove}
            testID={removeTestID}
            style={({ pressed }) => [styles.removeButton, pressed && styles.removeButtonPressed]}
            hitSlop={4}
            accessibilityRole="button"
            accessibilityLabel={removeAccessibilityLabel ?? 'Remove'}
          >
            <Text
              style={[
                styles.removeIcon,
                {
                  fontSize: isSmall ? tokens.fontSize.xs : tokens.fontSize.sm,
                  color: variantStyles.textColor,
                },
              ]}
            >
              {'\u00D7'}
            </Text>
          </Pressable>
        )}
      </View>
    );
  },
);

Badge.displayName = 'Badge';

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    borderRadius: tokens.radius.full,
  },
  sizeSm: {
    paddingHorizontal: tokens.spacing.xs,
    paddingVertical: tokens.spacing.xxs,
  },
  sizeMd: {
    paddingHorizontal: tokens.spacing.sm,
    paddingVertical: tokens.spacing.xs,
  },
  text: {
    fontWeight: '500',
    lineHeight: 16,
  },
  iconWrapper: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  iconSm: {
    marginRight: tokens.spacing.xxs,
  },
  iconMd: {
    marginRight: tokens.spacing.xs,
  },
  removeButton: {
    marginLeft: tokens.spacing.xxs,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: tokens.radius.full,
  },
  removeButtonPressed: {
    opacity: 0.6,
  },
  removeIcon: {
    fontWeight: '700',
    lineHeight: 16,
  },
});
